import { create } from 'zustand';
import type { Scene, SceneConfig, Model3D, Marker, FlyLine, Waypoint } from '@dtwin/shared';
import { generateId, DEFAULT_CAMERA } from '@dtwin/shared';
import type { PathCamera, CameraKeyFrame } from '../types/designer';
import { DEFAULT_SHADOW_SETTINGS } from '../utils/shadowExtent';
import type { AnnotationKind } from '../utils/annotations/primitives';
import { createChart3DObject, type Chart3DObject, type SpikeBoard3D, type SpikeChart3D, type SpikeHud3D } from '../types/chart3d';
import useHistoryStore from './historyStore';

export type { AnnotationKind };

type SceneSnapshotExtra = {
  environment?: unknown;
  effects?: unknown;
  lights?: unknown[];
  displayRegion?: unknown;
  showGrid?: boolean;
  showGround?: boolean;
  cameraStrategy?: unknown;
  savedCamera?: unknown;
  /** 2026-10-15（P0-④）：鸟瞰相机配置快照（撤销/重做） */
  birdsEyeCamera?: unknown;
  /** 2026-10-15（P0-④）：主相机设置快照 */
  mainCamera?: unknown;
  /** 2026-10-17（体积元素 S1 / 标绘层 S2）：纳入撤销快照 */
  volumes?: unknown;
  annotations?: unknown;
  /** P0：多图表数据模型纳入撤销快照 */
  chart3dObjects?: unknown;
};

function takeSnapshot(
  models: Model3D[],
  markers: Marker[],
  subObjectConfigs?: Record<string, SubObjectConfig[]>,
  extra?: SceneSnapshotExtra,
) {
  return {
    models: JSON.parse(JSON.stringify(models)),
    markers: JSON.parse(JSON.stringify(markers)),
    // 2026-09-14：小部件业务配置一并快照（软删除/重命名的撤销依赖它回滚后重放）
    subObjectConfigs: subObjectConfigs ? JSON.parse(JSON.stringify(subObjectConfigs)) : undefined,
    // E0：命令总线事务快照携带的扩展文档态（旧调用不传 → 不改变既有行为）
    ...(extra ?? {}),
  };
}

/** 从扩展文档态字段生成快照附加项（命令总线用） */
function snapshotExtra(state: {
  environment: unknown;
  effects: unknown;
  lights: unknown[];
  displayRegion: unknown;
  showGrid: boolean;
  showGround: boolean;
  cameraStrategy: unknown;
  savedCamera: unknown;
  birdsEyeCamera?: unknown;
  mainCamera?: unknown;
  volumes?: unknown;
  annotations?: unknown;
  chart3dObjects?: unknown;
}): SceneSnapshotExtra {
  return {
    environment: JSON.parse(JSON.stringify(state.environment)),
    effects: JSON.parse(JSON.stringify(state.effects)),
    lights: JSON.parse(JSON.stringify(state.lights)),
    displayRegion: state.displayRegion === null ? null : JSON.parse(JSON.stringify(state.displayRegion)),
    showGrid: state.showGrid,
    showGround: state.showGround,
    cameraStrategy: state.cameraStrategy,
    savedCamera: state.savedCamera === null ? null : JSON.parse(JSON.stringify(state.savedCamera)),
    ...(state.birdsEyeCamera !== undefined ? { birdsEyeCamera: JSON.parse(JSON.stringify(state.birdsEyeCamera)) } : {}),
    ...(state.mainCamera !== undefined ? { mainCamera: JSON.parse(JSON.stringify(state.mainCamera)) } : {}),
    ...(state.volumes !== undefined ? { volumes: JSON.parse(JSON.stringify(state.volumes)) } : {}),
    ...(state.annotations !== undefined ? { annotations: JSON.parse(JSON.stringify(state.annotations)) } : {}),
    ...(state.chart3dObjects !== undefined ? { chart3dObjects: JSON.parse(JSON.stringify(state.chart3dObjects)) } : {}),
  };
}

/** 快照 → 恢复补丁（E0：旧快照缺省扩展字段 = 不恢复，保持既有行为） */
function snapshotRestorePatch(snapshot: ReturnType<typeof takeSnapshot>) {
  return {
    models: snapshot.models,
    markers: snapshot.markers,
    ...(snapshot.subObjectConfigs ? { subObjectConfigs: snapshot.subObjectConfigs } : {}),
    ...(snapshot.environment !== undefined ? { environment: snapshot.environment } : {}),
    ...(snapshot.effects !== undefined ? { effects: snapshot.effects } : {}),
    ...(snapshot.lights !== undefined ? { lights: snapshot.lights } : {}),
    ...(snapshot.displayRegion !== undefined ? { displayRegion: snapshot.displayRegion } : {}),
    ...(snapshot.showGrid !== undefined ? { showGrid: snapshot.showGrid } : {}),
    ...(snapshot.showGround !== undefined ? { showGround: snapshot.showGround } : {}),
    ...(snapshot.cameraStrategy !== undefined ? { cameraStrategy: snapshot.cameraStrategy } : {}),
    ...(snapshot.savedCamera !== undefined ? { savedCamera: snapshot.savedCamera } : {}),
    ...(snapshot.birdsEyeCamera !== undefined ? { birdsEyeCamera: snapshot.birdsEyeCamera } : {}),
    ...(snapshot.mainCamera !== undefined ? { mainCamera: snapshot.mainCamera } : {}),
    ...(snapshot.volumes !== undefined ? { volumes: snapshot.volumes } : {}),
    ...(snapshot.annotations !== undefined ? { annotations: snapshot.annotations } : {}),
    ...(snapshot.chart3dObjects !== undefined ? { chart3dObjects: snapshot.chart3dObjects } : {}),
    dirty: true,
  };
}

// 历史快照节流：连续高频更新（如拖动滑块）在 500ms 窗口内只记录一次，
// 避免历史栈被刷爆并降低深拷贝开销
let lastSnapshotAt = 0;
// E0：命令总线事务期间挂起快照（整段事务 = 1 个撤销步，由总线在提交时统一压栈）
let historySuspended = false;
function shouldRecordHistory() {
  if (historySuspended) return false;
  const now = Date.now();
  if (now - lastSnapshotAt >= 500) {
    lastSnapshotAt = now;
    return true;
  }
  return false;
}

type WeatherType = 'clear' | 'rain' | 'snow' | 'cloudy';

/** I1：仿真参数包选择与阈值（随场景 config.simulation 保存） */
export interface SceneSimulationState {
  packs: string[];
  params: Record<string, { warn?: number; critical?: number }>;
}

interface EffectsState {
  dayNight: { enabled: boolean; timeOfDay: number; speed: number };
  /**
   * 2026-10-15（P0-⑤ 天空增强）：sky 扩展（向后兼容，缺省 procedural）
   * - type: procedural=程序化渐变（默认）/ texture=贴图 / color=纯色
   * - opacity: 天空不透明度（0~1，与背景色混合）
   * - lensFlareIntensity: 程序化太阳光晕强度（0=关）
   * - timeMode: auto=跟随系统时钟（运行时，不标 dirty）/ manual=面板时间滑杆（随场景保存）
   */
  sky: {
    enabled: boolean;
    sunElevation: number;
    sunAzimuth: number;
    type?: 'procedural' | 'texture' | 'color';
    color?: string;
    opacity?: number;
    textureType?: 'hdr' | 'cube';
    textureUrl?: string;
    textureIntensity?: number;
    lensFlareIntensity?: number;
    timeMode?: 'auto' | 'manual';
    /** 2026-10-16（P2 夜空）：星空与月相（缺省关/向后兼容） */
    starsEnabled?: boolean;
    starDensity?: number;
    /** 月相 0=新月 0.5=满月 1=新月 */
    moonPhase?: number;
    /** 月光强度 0=不显示月亮 */
    moonIntensity?: number;
    /** manual 模式时间（0~24 小时，持久化；显示如 16:00） */
    timeHours?: number;
  };
  clouds: { enabled: boolean; coverage: number; density: number; speed: number };
  weather: { type: WeatherType; intensity: number; wind: [number, number] };
  water: { enabled: boolean };
  atmosphere: { enabled: boolean; intensity: number; color: string };
  particles: {
    enabled: boolean;
    emitter: 'point' | 'sphere' | 'box' | 'cone' | 'ring' | 'rain' | 'snow' | 'flow';
    emitterSize: number;
    count: number;
    lifetime: number;
    speed: number;
    speedVariation: number;
    size: number;
    sizeVariation: number;
    color: string;
    colorEnd: string;
    opacity: number;
    opacityEnd: number;
    blendMode: 'normal' | 'additive';
    gravity: [number, number, number];
    spread: number;
    loop: boolean;
    offset: [number, number, number];
  };
    postprocessing: {
      // 2026-10-16（P1-1 渲染/后期接线）：曝光与分辨率缩放（原硬编码 toneMappingExposure=1.2 / dpr 默认）
      exposure: number;
      resolutionScale: number;
      bloom: { enabled: boolean; intensity: number; threshold: number; smoothing: number };
      dof: { enabled: boolean; focusDistance: number; focalLength: number; aperture: number };
      grain: { enabled: boolean; intensity: number };
      vignette: { enabled: boolean; darkness: number; offset: number };
      ssao: { enabled: boolean; intensity: number; radius: number; distance: number };
      antialiasing: { enabled: boolean; mode: 'smaa' | 'fxaa' };
      outline: { enabled: boolean; color: string; width: number };
    };
}

/**
 * 2026-10-17（标绘层 S2/S3）：3D 标绘对象（点/文字/折线/面/矩形/圆 + 椭圆/扇形/自由线/自由面/曲线 + 箭头族）
 * 数据模型对齐 Cesium 标绘生态 {type, points, style}；随 scene.config.annotations 保存。
 * points=关键点=编辑手柄位；生成类图元（矩形/圆/椭圆/扇形/箭头/曲线）由 utils/annotations/primitives 的 outlineFor 重建。
 */
export interface AnnotationBindingRule { min: number; max: number; color: string }
export interface SceneAnnotation {
  id: string;
  kind: AnnotationKind;
  name: string;
  points: [number, number, number][];
  planeMode: 'ground' | 'model' | 'free';
  style: {
    lineColor: string;
    lineWidth: number;
    dashed: boolean;
    fillColor?: string;
    fillOpacity?: number;
    text?: { content: string; fontSize: number; color: string; bg?: string };
    /** 箭头族：头大小/干宽倍率（默认 1） */
    arrow?: { headSize?: number; tailWidth?: number };
  };
  visible: boolean;
  locked?: boolean;
  /** S3：数据绑定（文字显示设备点位值；rules 按阈值改颜色） */
  binding?: {
    deviceId?: string;
    pointKey?: string;
    rules?: AnnotationBindingRule[];
  };
  /** S3：生长/淡入动画（预览/发布与编辑器同源） */
  animation?: { grow?: boolean; fade?: boolean };
}

/**
 * 2026-10-17（体积元素 S1/S4）：空间效果体（区域覆盖=Unity Volume 语义；光柱=godRay；雾盒/云团=可见体积体）
 * 运行时权重见 utils/volumeWeight；envOverride 不落场景（运行时覆盖），几何参数随 scene.config.volumes 保存。
 * S4：fogBox=屏幕空间 ray-box raymarch-lite（低分辨率步进+抖动）；cloudImpostor=面向相机噪声贴片叠加。
 */
export type VolumeKind = 'envOverride' | 'godRay' | 'fogBox' | 'cloudImpostor';
export interface VolumeElement {
  id: string;
  kind: VolumeKind;
  name: string;
  enabled: boolean;
  shape: 'box' | 'sphere' | 'cylinder';
  position: [number, number, number];
  /** box: 半长；sphere: [半径, -, -]；cylinder: [半径, 半高, -] */
  size: [number, number, number];
  /** 边界外开始混合的距离（默认 size 最大半长的 20%） */
  blendDistance?: number;
  /** 多体积重叠时插值权重（越大越主导） */
  priority?: number;
  /** 区域覆盖字段（同 environment 名称；P0：雾/环境光/平行光/曝光） */
  envs?: Record<string, number | boolean | string>;
  /** 光柱参数 */
  ray?: { color: string; intensity: number; noiseSpeed: number; noiseScale: number };
  /** S4：雾盒/云团参数（部分覆盖；缺省由组件默认值补齐；steps=雾盒采样步数，性能档联动） */
  vol?: {
    /** 雾密度 / 云量（0~1） */
    density?: number;
    color?: string;
    noiseScale?: number;
    noiseSpeed?: number;
    opacity?: number;
    /** 雾盒 raymarch 步数（8~64；性能档联动：高 48/中 32/低 16） */
    steps?: number;
  };
}

/** 场景灯光（P0-⑤ §8.2：SceneLights 渲染；内置太阳 id='directional-default' 由 SunLight 承担） */
export interface SceneLight {
  id: string;
  name: string;
  type: 'ambient' | 'directional' | 'point' | 'spot';
  enabled: boolean;
  color: string;
  intensity: number;
  position?: [number, number, number];
  target?: [number, number, number];
  distance?: number;
  decay?: number;
  angle?: number;
  penumbra?: number;
  castShadow?: boolean;
}

interface CarouselState {
  enabled: boolean;
  autoPlay: boolean;
  currentIndex: number;
  interval: number;
  activeMarkerId: string | null;
}

interface ClippingState {
  enabled: boolean;
  axis: 'x' | 'y' | 'z';
  position: number;
}

interface AnimationState {
  clipName: string | null;
  isPlaying: boolean;
  speed: number;
}

interface MeasurementPoint {
  position: { x: number; y: number; z: number };
  id: string;
}

interface Measurement {
  id: string;
  type: 'distance' | 'angle' | 'area';
  points: MeasurementPoint[];
  value: number;
  unit: string;
  /** 测量模式扩展（boxSize 等），兼容新测量体系 */
  mode?: 'distance' | 'angle' | 'area' | 'boxSize';
  /** 完整计算结果（斜距/水平/高差/角度/面积/长宽高） */
  result?: Record<string, number>;
  visible?: boolean;
}

import type { SubObjectNode, SubObjectConfig } from '@dtwin/shared';

/** 2026-09-08｜修复：在构件树中按 uuid 查找节点的 children 索引路径（跨会话恢复重映射用） */
function findNodePath(nodes: SubObjectNode[] | undefined, uuid: string): number[] | null {
  if (!nodes) return null;
  for (const n of nodes) {
    if (n.uuid === uuid) return n.path ?? null;
    const child = findNodePath(n.children, uuid);
    if (child) return child;
  }
  return null;
}

interface SceneState {
  currentScene: Scene | null;
  models: Model3D[];
  markers: Marker[];
  flyLines: FlyLine[];
  waypoints: Waypoint[];
  /** 2026-08-31｜组合模型自动解析结果：modelId → 构件树（Group/Mesh 层级，模型加载后生成） */
  subObjectsMap: Record<string, SubObjectNode[]>;
  /** 2026-09-08｜阶段5：构件业务配置（显隐/变换/材质，随工程保存；modelId → 配置数组） */
  subObjectConfigs: Record<string, SubObjectConfig[]>;
  /** 2026-08-31｜第一人称自动浏览路线（[x, z] 序列，y 由贴地射线自动保持人视高度）；
   * 编辑器「漫游→自动浏览路线」选点部署，演示/发布页自动游览读取 */
  browseRoute: [number, number][];
  cameraPathPlaying: boolean;
  cameraPathSpeed: number;
  selectedModelId: string | null;
  /** 2026-09-08｜阶段3：选中的子构件（组合模型内部节点，uuid 定位；null=选中顶层模型） */
  selectedSubObject: { modelId: string; uuid: string } | null;
  /** 2026-09-14：部件多选（快速选中同源部件）——selectedSubObject 始终为数组末位（主选中，兼容 gizmo/属性面板） */
  selectedSubObjects: { modelId: string; uuid: string }[];
  /** 2026-09-14：组合模型子构件解锁状态（默认锁定=整体操作；解锁后可展开树/选子构件/编辑部件） */
  subObjectUnlocked: Record<string, boolean>;
  /** 2026-09-27：左侧「模型」面板展开构件树的模型 id（刷新按场景持久化恢复；null=未展开） */
  expandedModelId: string | null;
  /** 2026-09-14：模型重载令牌（上传贴图后 bump → 清 loader 缓存并重挂载，使贴图生效） */
  modelReloadToken: Record<string, number>;
  /** 2026-09-14：待归一化的新导入模型标记（仅"刚上传导入"的模型在首次加载后做尺寸归一化，
   *  已保存/原有模型永不触发——避免误缩源场景正常大模型） */
  normalizePending: Record<string, boolean>;
  /** 2026-09-14：模型加载进度（加载条 UI）——phase：download=下载中(有真实进度)/parse=解析中(FBXLoader 同步解析无进度事件) */
  modelLoading: { modelId: string; progress: number; active: boolean; phase?: 'download' | 'parse' } | null;
  transformMode: 'translate' | 'rotate' | 'scale';
  animation: AnimationState;
  clipping: ClippingState;
  carousel: CarouselState;
  environment: {
    backgroundColor: string;
    /** 2026-10-15（P0 场景属性面板）：雾开关（新增，默认关；关闭时保留雾色/浓度数值但不生效） */
    fogEnabled: boolean;
    /**
     * 2026-10-16（P1-2 雾增强，P1 架构 §七 契约）：雾参数（旧场景 fogColor/fogDensity 由 loadEnvironment 迁移）
     * - density：浓度（线性雾 near/far 随 sceneRadius 缩放，0.008=基准）
     * - startDistance/endDistance：显式起止距离（米，0=自动随 sceneRadius）
     * - maxOpacity：最大不透明度（<1 时雾不到全遮 → 远景仍可见）
     * - affectedBySky：雾色跟随背景色
     * - heightFalloff/heightOffset：高度雾（P2 提供，暂预留）
     */
    fog: {
      density: number;
      color: string;
      startDistance: number;
      endDistance: number;
      maxOpacity: number;
      affectedBySky: boolean;
      heightFalloff: number;
      heightOffset: number;
    };
    sceneRadius: number;
    /** 2026-10-16（P2 世界原点）：导航中心（命令/定位/自动适配的参考点；随场景保存） */
    worldOrigin: [number, number, number];
    ambientIntensity: number;
    ambientColor: string;
    directionalIntensity: number;
    directionalColor: string;
    environmentMapUrl: string | null;
    /** 2026-10-15（P0 阴影修复）：平行光（太阳）阴影参数；auto=阴影相机范围自适应场景半径 */
    shadow: import('../utils/shadowExtent').ShadowSettings;
  };
  effects: EffectsState;
  lights: SceneLight[];
  /** 2026-10-17（体积元素 S1）：空间效果体（随 scene.config.volumes 保存） */
  volumes: VolumeElement[];
  /** 2026-10-17（标绘层 S2）：3D 标绘对象（随 scene.config.annotations 保存） */
  annotations: SceneAnnotation[];
  /** S0-A：场景内 3D 图表原型（随 scene.config.spikeChart3D 保存） */
  spikeChart3D: SpikeChart3D | null;
  /** S0-B：图表贴屏/公告板（ECharts CanvasTexture） */
  spikeBoard3D: SpikeBoard3D | null;
  /** S0-C：Html/CSS3D HUD 面板 */
  spikeHud3D: SpikeHud3D | null;
  /** P0：场景内多图表对象（随 scene.config.chart3dObjects 保存） */
  chart3dObjects: Chart3DObject[];
  isLoading: boolean;
  measurementMode: 'distance' | 'angle' | 'area' | null;
  measurements: Measurement[];
  /** 测量草稿点（点击 3D 场景地面时逐步添加，计数满足后自动生成 Measurement） */
  measurementDraftPoints: MeasurementPoint[];
  visibility: Record<string, boolean>;
  locked: Record<string, boolean>;
  boundedMode: boolean;
  showGrid: boolean;
  showGround: boolean;
  /** 相机状态策略：auto=自动适配模型；current=保存的当前视角；custom=自定义坐标 */
  cameraStrategy: 'auto' | 'current' | 'custom';
  /** 保存的相机状态（current/custom 策略使用） */
  savedCamera: { position: [number, number, number]; target: [number, number, number] } | null;
  /** 2026-10-15（P0-④）：鸟瞰相机配置（随场景 config.birdsEyeCamera 保存） */
  birdsEyeCamera: import('../types/designer').BirdsEyeCamera;
  /** 2026-10-15（P0-④）：主相机设置（editor | birdsEye | path；随 config.mainCamera 保存） */
  mainCamera: import('../types/designer').MainCameraSetting;
  /** I1：仿真参数包选择与阈值（随场景 config.simulation 保存） */
  simulation: SceneSimulationState | null;
  /** 是否有未保存的修改（编辑器保存按钮 * 提示，对齐大屏设计器） */
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  setCameraStrategy: (strategy: 'auto' | 'current' | 'custom') => void;
  setSavedCamera: (camera: { position: [number, number, number]; target: [number, number, number] }) => void;
  /** 2026-10-15（P0-④）：更新鸟瞰相机配置（部分字段） */
  setBirdsEyeCamera: (patch: Partial<import('../types/designer').BirdsEyeCamera>) => void;
  /** 2026-10-15（P0-④）：设置主相机（editor/birdsEye/path） */
  setMainCamera: (setting: import('../types/designer').MainCameraSetting) => void;
  setSimulation: (simulation: SceneSimulationState | null) => void;
  setScene: (scene: Scene) => void;
  setModels: (models: Model3D[]) => void;
  addModel: (model: Omit<Model3D, 'sceneId'> & { id?: string }) => void;
  updateModel: (id: string, updates: Partial<Model3D>, recordHistory?: boolean) => void;
  removeModel: (id: string) => void;
  selectModel: (id: string | null) => void;
  /** 2026-09-08｜阶段3：选中子构件（uuid；null=清除构件选中回到顶层模型） */
  selectSubObject: (sub: { modelId: string; uuid: string } | null) => void;
  /** 2026-09-14：批量设置部件选中（多选；空数组=清除） */
  selectSubObjects: (list: { modelId: string; uuid: string }[]) => void;
  /** 2026-09-14：设置组合模型子构件解锁状态（解锁后方可展开树/选子构件） */
  setSubObjectUnlocked: (modelId: string, unlocked: boolean) => void;
  /** 2026-09-27：设置左侧「模型」面板展开构件树的模型（null=收起；刷新恢复持久化） */
  setExpandedModelId: (id: string | null) => void;
  /** 2026-09-27：整体恢复组合模型解锁状态（刷新恢复用；调用方先过滤失效模型 id） */
  setSubObjectUnlockedMap: (map: Record<string, boolean>) => void;
  /** 2026-09-14：bump 模型重载令牌（贴图上传后触发模型重新加载） */
  bumpModelReload: (modelId: string) => void;
  /** 2026-09-14：标记新导入模型待归一化（上传成功后调用） */
  markNormalizePending: (modelId: string) => void;
  /** 2026-09-14：清除归一化标记（归一化完成/判定无需后调用） */
  clearNormalizePending: (modelId: string) => void;
  /** 2026-09-14：设置模型加载进度（null=清空） */
  setModelLoading: (v: { modelId: string; progress: number; active: boolean; phase?: 'download' | 'parse' } | null) => void;
  /** 2026-09-14：推送含小部件配置的历史快照（软删除/重命名等部件操作撤销用） */
  pushSubObjectSnapshot: () => void;
  /** 2026-09-08｜阶段5：整体设置构件配置（工程加载恢复） */
  setSubObjectConfigs: (configs: Record<string, SubObjectConfig[]>) => void;
  /** 2026-09-08｜阶段5：更新单个构件配置（变换/材质/显隐），不存在则追加 */
  updateSubObjectConfig: (modelId: string, cfg: Partial<SubObjectConfig> & { uuid: string; name?: string }) => void;
  /** 剪切板：保存被剪切的模型，用于粘贴 */
  clipboardModels: Model3D[];
  cutModel: (id: string) => void;
  pasteModel: () => void;
  clearClipboard: () => void;
  setTransformMode: (mode: 'translate' | 'rotate' | 'scale') => void;
  /** 图层排序：上移/下移/置顶/置底（调整 models 渲染顺序） */
  moveModel: (id: string, dir: 'up' | 'down' | 'top' | 'bottom') => void;
  setEnvironment: (env: Partial<SceneState['environment']>, markDirty?: boolean) => void;
  /**
   * 2026-10-15（P0 场景属性面板）：载入场景的环境配置（编辑器/预览/发布统一入口）。
   * 旧场景 config.environment 无 `fogEnabled` 字段 → 保持历史行为（有雾）；新场景默认无雾。
   */
  loadEnvironment: (raw: Record<string, unknown> | null | undefined) => void;
  setEffects: (effects: Partial<EffectsState>, markDirty?: boolean) => void;
  addLight: (light: Omit<SceneLight, 'id'>) => void;
  /** 2026-10-17（体积元素 S1） */
  addVolume: (v: Omit<VolumeElement, 'id'>) => void;
  updateVolume: (id: string, updates: Partial<VolumeElement>) => void;
  removeVolume: (id: string) => void;
  /** 2026-10-17（标绘层 S2） */
  addAnnotation: (a: Omit<SceneAnnotation, 'id'>) => string;
  updateAnnotation: (id: string, updates: Partial<SceneAnnotation>) => void;
  removeAnnotation: (id: string) => void;
  setSpikeChart3D: (chart: SpikeChart3D | null) => void;
  updateSpikeChart3D: (id: string, updates: Partial<SpikeChart3D>) => void;
  setSpikeBoard3D: (board: SpikeBoard3D | null) => void;
  updateSpikeBoard3D: (id: string, updates: Partial<SpikeBoard3D>) => void;
  setSpikeHud3D: (hud: SpikeHud3D | null) => void;
  updateSpikeHud3D: (id: string, updates: Partial<SpikeHud3D>) => void;
  setChart3DObjects: (charts: Chart3DObject[]) => void;
  addChart3D: (chart: Omit<Chart3DObject, 'id'> & { id?: string }) => string;
  updateChart3D: (id: string, updates: Partial<Chart3DObject>, recordHistory?: boolean) => void;
  removeChart3D: (id: string) => void;
  updateLight: (id: string, updates: Partial<SceneLight>) => void;
  removeLight: (id: string) => void;
  toggleLight: (id: string) => void;
  undo: () => void;
  redo: () => void;
  setAnimation: (anim: Partial<AnimationState>) => void;
  setClipping: (clip: Partial<ClippingState>) => void;
  setCarousel: (carousel: Partial<CarouselState>) => void;
  addMarker: (marker: Omit<Marker, 'id'>) => void;
  setMarkers: (markers: Marker[]) => void;
  setWaypoints: (waypoints: Waypoint[]) => void;
  setModelSubObjects: (modelId: string, tree: SubObjectNode[]) => void;
  clearModelSubObjects: (modelId: string) => void;
  /** 2026-09-08｜阶段1：查询构件 uuid 的整条父链（含自身），用于树面板自动展开定位 */
  getAncestorIds: (modelId: string, uuid: string) => string[];
  setBrowseRoute: (route: [number, number][]) => void;
  addBrowsePoint: (p: [number, number]) => void;
  removeBrowsePoint: (index: number) => void;
  addWaypoint: (waypoint: Omit<Waypoint, 'id'>) => void;
  removeWaypoint: (id: string) => void;
  setCameraPathPlaying: (playing: boolean) => void;
  setCameraPathSpeed: (speed: number) => void;
  setFlyLines: (lines: FlyLine[]) => void;
  addFlyLine: (line: Omit<FlyLine, 'id'>) => void;
  updateFlyLine: (id: string, updates: Partial<FlyLine>) => void;
  removeFlyLine: (id: string) => void;
  updateCamera: (camera: Partial<SceneConfig['camera']>) => void;
  setMeasurementMode: (mode: 'distance' | 'angle' | 'area' | null) => void;
  addMeasurementPoint: (point: { x: number; y: number; z: number }) => void;
  clearMeasurementDraft: () => void;
  addMeasurement: (measurement: Measurement) => void;
  removeMeasurement: (id: string) => void;
  setMeasurements: (measurements: Measurement[]) => void;
  clearMeasurements: () => void;
  toggleVisibility: (id: string) => void;
  toggleLocked: (id: string) => void;
  setLocked: (id: string, locked: boolean) => void;
  updateWaypoint: (id: string, updates: Partial<Waypoint>) => void;
  setBoundedMode: (bounded: boolean) => void;
  /** 2026-10-06：展示区域（框选有限展示范围；null=全场景无限区域） */
  displayRegion: { x1: number; z1: number; x2: number; z2: number } | null;
  /** 正在框选展示区域（鼠标左键拉虚线框） */
  regionSelecting: boolean;
  /** G2：正在框选区域生成（批量 AI 操作；DOM 遮罩层） */
  regionGenSelecting: boolean;
  setDisplayRegion: (r: { x1: number; z1: number; x2: number; z2: number } | null) => void;
  setRegionSelecting: (v: boolean) => void;
  setRegionGenSelecting: (v: boolean) => void;
  /** 2026-10-07：模型轴心修正偏移（content-local 平移；第三方模型视觉中心≠原点时用） */
  pivotOffsets: Record<string, [number, number, number]>;
  setPivotOffset: (modelId: string, offset: [number, number, number] | null) => void;
  setPivotOffsets: (map: Record<string, [number, number, number]>) => void;
  setShowGrid: (show: boolean) => void;
  setShowGround: (show: boolean) => void;
  /** 路径相机（轨道相机）集合——持久化到场景 config.pathCameras */
  pathCameras: PathCamera[];
  setPathCameras: (cameras: PathCamera[]) => void;
  addPathCamera: (camera: Omit<PathCamera, 'id'>) => string;
  updatePathCamera: (id: string, updates: Partial<PathCamera>) => void;
  removePathCamera: (id: string) => void;
  addKeyFrame: (cameraId: string, frame: CameraKeyFrame) => void;
  updateKeyFrame: (cameraId: string, index: number, updates: Partial<CameraKeyFrame>) => void;
  removeKeyFrame: (cameraId: string, index: number) => void;
}

const defaultEnv = {
  backgroundColor: '#87CEEB',
  // 2026-10-15（P0 场景属性面板）：新场景默认无雾（旧场景由 loadEnvironment 兼容为有雾）
  fogEnabled: false,
  // 2026-10-16（P1-2 雾增强）：雾参数（density 0.008=基准；起止距离 0=自动；maxOpacity 1=全遮）
  fog: {
    density: 0.008,
    color: '#c8e4f8',
    startDistance: 0,
    endDistance: 0,
    maxOpacity: 1,
    affectedBySky: false,
    heightFalloff: 0,
    heightOffset: 0,
  },
  sceneRadius: 100,
  worldOrigin: [0, 0, 0] as [number, number, number],
  ambientIntensity: 0.6,
  ambientColor: '#e8f0ff',
  directionalIntensity: 1.2,
  directionalColor: '#fffbe6',
  environmentMapUrl: null as string | null,
  // 2026-10-15（P0 阴影修复）：默认自动适配范围 + 2048 贴图 + 强度 1
  shadow: { ...DEFAULT_SHADOW_SETTINGS },
};

const defaultEffects: EffectsState = {
  dayNight: { enabled: false, timeOfDay: 12, speed: 0.1 },
  sky: {
    enabled: false,
    sunElevation: 0.5,
    sunAzimuth: 0.8,
    // 2026-10-15（P0-⑤）：天空扩展字段默认（procedural 程序化；auto 时间跟随系统时钟）
    type: 'procedural',
    color: '#87CEEB',
    opacity: 1,
    textureType: 'hdr',
    textureIntensity: 1,
    lensFlareIntensity: 0.6,
    timeMode: 'auto',
    timeHours: 12,
    // P2 夜空：默认关（不改变既有场景）
    starsEnabled: false,
    starDensity: 3000,
    moonPhase: 0.5,
    moonIntensity: 0,
  },
  clouds: { enabled: false, coverage: 0.4, density: 0.6, speed: 0.05 },
  weather: { type: 'clear', intensity: 0.5, wind: [0.3, 0.1] },
  water: { enabled: false },
  atmosphere: { enabled: false, intensity: 0.5, color: '#88bbff' },
  particles: {
    enabled: false, emitter: 'sphere', emitterSize: 30, count: 3000,
    lifetime: 5, speed: 3, speedVariation: 1, size: 0.8, sizeVariation: 0.3,
    color: '#ffffff', colorEnd: '#88bbff', opacity: 1, opacityEnd: 0,
    blendMode: 'additive', gravity: [0, -5, 0], spread: 360, loop: true,
    offset: [0, 0, 0],
  },
  postprocessing: {
    // 2026-10-16（P1-1）：曝光（=原硬编码 1.2）+ 分辨率缩放（1=按设备 DPR）
    exposure: 1.2,
    resolutionScale: 1,
    bloom: { enabled: false, intensity: 0.15, threshold: 0.8, smoothing: 0.9 },
    dof: { enabled: false, focusDistance: 10, focalLength: 0.1, aperture: 0.05 },
    grain: { enabled: false, intensity: 0.05 },
    vignette: { enabled: false, darkness: 0.5, offset: 0.2 },
    ssao: { enabled: false, intensity: 1, radius: 0.5, distance: 0.5 },
    // 闪动排查（2026-08-11）已排除 SMAA shimmer 为根因（最终定性为远程桌面传输问题）→ 恢复默认启用。
    // 启用时 gl.antialias 回退 false，由 EffectComposer 的 SMAA/FXAA 提供抗锯齿
    antialiasing: { enabled: true, mode: 'smaa' },
    outline: { enabled: false, color: '#00ffff', width: 3 },
  },
};

const defaultLights: SceneLight[] = [
  { id: 'ambient-default', name: '环境光', type: 'ambient', enabled: true, color: '#e8f0ff', intensity: 0.6 },
  { id: 'directional-default', name: '平行光', type: 'directional', enabled: true, color: '#fffbe6', intensity: 1.2, position: [5, 8, 5], castShadow: true },
];

const useSceneStore = create<SceneState>((set, get) => ({
  currentScene: null,
  models: [],
  markers: [],
  flyLines: [],
  waypoints: [],
  browseRoute: [],
  subObjectsMap: {},
  cameraPathPlaying: false,
  cameraPathSpeed: 1,
  pathCameras: [],
  // 2026-10-15（P0-④ 鸟瞰相机）：默认关闭；启用后主相机可切鸟瞰（见 BirdsEyeCameraRig）
  birdsEyeCamera: {
    enabled: false,
    name: '鸟瞰相机',
    viewType: 'perspective',
    zDistance: 50,
    screenOffset: [0, 0],
    zoomMin: 5,
    zoomMax: 200,
    polarMin: 1,
    polarMax: 85,
    fov: 50,
    orthoZoom: 50,
    autoRotate: false,
    autoRotateSpeed: 2,
    presets: [],
  },
  mainCamera: { type: 'editor' },
  selectedModelId: null,
  selectedSubObject: null,
  selectedSubObjects: [],
  subObjectUnlocked: {},
  expandedModelId: null,
  modelReloadToken: {},
  normalizePending: {},
  modelLoading: null,
  subObjectConfigs: {},
  transformMode: 'translate',
  animation: { clipName: null, isPlaying: false, speed: 1 },
  clipping: { enabled: false, axis: 'y', position: 0 },
  carousel: { enabled: false, autoPlay: false, currentIndex: 0, interval: 3, activeMarkerId: null },
  environment: { ...defaultEnv },
  effects: { ...defaultEffects },
  lights: [...defaultLights],
  volumes: [],
  annotations: [],
  spikeChart3D: null,
  spikeBoard3D: null,
  spikeHud3D: null,
  chart3dObjects: [],
  isLoading: false,
  measurementMode: null,
  measurements: [],
  measurementDraftPoints: [],
  visibility: {},
  locked: {},
  boundedMode: false,
  displayRegion: null,
  regionSelecting: false,
  regionGenSelecting: false,
  pivotOffsets: {},
  showGrid: true,
  showGround: true,
  cameraStrategy: 'auto',
  savedCamera: null,
  simulation: null,
  dirty: false,

  setCameraStrategy: (strategy) => set({ cameraStrategy: strategy, dirty: true }),

  setSavedCamera: (camera) => set({ savedCamera: camera, dirty: true }),

  // 2026-10-15（P0-④ 鸟瞰相机）：启用/参数更新（随场景保存）；主相机切换
  setBirdsEyeCamera: (patch) =>
    set((state) => ({ birdsEyeCamera: { ...state.birdsEyeCamera, ...patch }, dirty: true })),
  setMainCamera: (setting) => set({ mainCamera: setting, dirty: true }),
  setSimulation: (simulation) => set({ simulation, dirty: true }),

  setDirty: (dirty) => set({ dirty }),

  setScene: (scene) => {
    // 2026-09-08调试：加载时恢复过的构件配置数量（便于排查）
    const cfgCount = Object.entries(scene.config?.subObjectConfigs ?? {}).map(([mid, arr]) => `${mid.slice(0, 6)}:${arr.length}`).join(', ');
    if (cfgCount) console.log('[LOAD_SUBOBJECTS]', cfgCount);
    return set({
      currentScene: scene,
      dirty: false,
      // 2026-09-08｜阶段5：恢复构件业务配置（模型加载后 uuid 应用于显隐/变换/材质）
      subObjectConfigs: scene.config?.subObjectConfigs ?? {},
      // I1：恢复仿真参数包（config.simulation）
      simulation: (scene.config as { simulation?: SceneSimulationState } | undefined)?.simulation ?? null,
    });
  },

  setModels: (models) =>
    set({
      models,
      // 2026-09-27（审计 FE-A02 修复）：从持久化模型水合锁定态——
      // 此前 locked map 刷新后为空，场景树锁图标不恢复且首次点击"解锁"反而再锁一次
      locked: models.reduce<Record<string, boolean>>((acc, m) => {
        if ((m as { locked?: boolean }).locked) acc[m.id] = true;
        return acc;
      }, {}),
    }),

  addModel: (model) => {
    if (shouldRecordHistory()) {
      const snapshot = takeSnapshot(get().models, get().markers, get().subObjectConfigs);
      useHistoryStore.getState().pushSnapshot(snapshot);
    }
    const id = model.id || generateId();
    const newModel: Model3D = {
      ...model,
      id,
      sceneId: get().currentScene?.id ?? '',
    };
    set((state) => ({ models: [...state.models, newModel], dirty: true }));
  },

  updateModel: (id, updates, recordHistory = true) => {
    // 缩放钳制：scale 任一轴 ≤0 或无理会黑屏（矩阵奇异 NaN），统一拦截为最小可见值
    const safeUpdates: Partial<Model3D> = { ...updates };
    if (Array.isArray(safeUpdates.scale)) {
      safeUpdates.scale = (safeUpdates.scale as [number, number, number]).map((v) =>
        v === 0 || !Number.isFinite(v) ? 0.001 : v,
      ) as [number, number, number];
    }
    if (recordHistory && shouldRecordHistory()) {
      const snapshot = takeSnapshot(get().models, get().markers, get().subObjectConfigs);
      useHistoryStore.getState().pushSnapshot(snapshot);
    }
    set((state) => ({
      models: state.models.map((m) => (m.id === id ? { ...m, ...safeUpdates } : m)),
      dirty: true,
    }));
  },

  removeModel: (id) => {
    if (shouldRecordHistory()) {
      const snapshot = takeSnapshot(get().models, get().markers, get().subObjectConfigs);
      useHistoryStore.getState().pushSnapshot(snapshot);
    }
    set((state) => ({
      models: state.models.filter((m) => m.id !== id),
      selectedModelId: state.selectedModelId === id ? null : state.selectedModelId,
      expandedModelId: state.expandedModelId === id ? null : state.expandedModelId,
      dirty: true,
    }));
  },

  selectModel: (id) => set({ selectedModelId: id }),
  selectSubObject: (sub) => set({ selectedSubObject: sub, selectedSubObjects: sub ? [sub] : [] }),
  selectSubObjects: (list) => set({ selectedSubObjects: list, selectedSubObject: list.length ? list[list.length - 1] : null }),
  setSubObjectUnlocked: (modelId, unlocked) =>
    set((state) => ({ subObjectUnlocked: { ...state.subObjectUnlocked, [modelId]: unlocked } })),
  setExpandedModelId: (id) => set({ expandedModelId: id }),
  setSubObjectUnlockedMap: (map) => set({ subObjectUnlocked: { ...map } }),
  bumpModelReload: (modelId) =>
    set((state) => ({ modelReloadToken: { ...state.modelReloadToken, [modelId]: (state.modelReloadToken[modelId] ?? 0) + 1 } })),
  setModelLoading: (v) => set({ modelLoading: v }),
  markNormalizePending: (modelId) =>
    set((state) => ({ normalizePending: { ...state.normalizePending, [modelId]: true } })),
  clearNormalizePending: (modelId) =>
    set((state) => {
      const next = { ...state.normalizePending };
      delete next[modelId];
      return { normalizePending: next };
    }),
  pushSubObjectSnapshot: () => {
    if (historySuspended) return; // E0：事务内由总线统一记录
    const st = get();
    useHistoryStore.getState().pushSnapshot(takeSnapshot(st.models, st.markers, st.subObjectConfigs));
  },
  setSubObjectConfigs: (configs) => set({ subObjectConfigs: configs }),
  updateSubObjectConfig: (modelId, cfg) =>
    set((state) => {
      const list = state.subObjectConfigs[modelId] ?? [];
      const idx = list.findIndex((c) => c.uuid === cfg.uuid);
      const next = [...list];
      // 2026-09-08｜修复：自动补 children 索引路径（跨会话恢复 uuid 失配时按 path 重映射）
      let path = cfg.path;
      if (!path) {
        const found = findNodePath(state.subObjectsMap[modelId], cfg.uuid);
        if (found) path = found;
      }
      const withPath = path ? { ...cfg, path } : cfg;
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...withPath };
      } else {
        next.push({ ...withPath, uuid: cfg.uuid, name: cfg.name ?? '', visible: cfg.visible ?? true });
      }
      return { subObjectConfigs: { ...state.subObjectConfigs, [modelId]: next }, dirty: true };
    }),

  clipboardModels: [],

  cutModel: (id) => {
    const model = get().models.find((m) => m.id === id);
    if (!model) return;
    if (shouldRecordHistory()) {
      const snapshot = takeSnapshot(get().models, get().markers, get().subObjectConfigs);
      useHistoryStore.getState().pushSnapshot(snapshot);
    }
    set({
      clipboardModels: [model],
      models: get().models.filter((m) => m.id !== id),
      selectedModelId: get().selectedModelId === id ? null : get().selectedModelId,
      dirty: true,
    });
  },

  pasteModel: () => {
    const src = get().clipboardModels[0];
    if (!src) return;
    if (shouldRecordHistory()) {
      const snapshot = takeSnapshot(get().models, get().markers, get().subObjectConfigs);
      useHistoryStore.getState().pushSnapshot(snapshot);
    }
    const clone: Model3D = {
      ...src,
      id: generateId(),
      sceneId: get().currentScene?.id ?? src.sceneId,
      name: `${src.name}`,
      position: [src.position[0] + 2, src.position[1], src.position[2]],
    };
    set((state) => ({ models: [...state.models, clone], selectedModelId: clone.id, dirty: true }));
  },

  clearClipboard: () => set({ clipboardModels: [] }),

  setTransformMode: (mode) => set({ transformMode: mode }),

  moveModel: (id, dir) =>
    set((state) => {
      const arr = [...state.models];
      const idx = arr.findIndex((m) => m.id === id);
      if (idx < 0) return state;
      const [item] = arr.splice(idx, 1);
      if (dir === 'up') arr.splice(Math.max(0, idx - 1), 0, item);
      else if (dir === 'down') arr.splice(Math.min(arr.length, idx + 1), 0, item);
      else if (dir === 'top') arr.unshift(item);
      else arr.push(item);
      return { models: arr, dirty: true };
    }),

  setEnvironment: (env, markDirty = true) =>
    set((state) => ({ environment: { ...state.environment, ...env }, ...(markDirty ? { dirty: true } : {}) })),

  // 2026-10-15（P0 场景属性面板）：雾开关为新增字段——旧场景（无该字段）按历史行为置 true，
  // 避免"升级后所有老场景的雾消失"；新场景 defaultEnv.fogEnabled=false，保存后字段存在即按保存值。
  loadEnvironment: (raw) =>
    set((state) => {
      if (!raw || typeof raw !== 'object') return {};
      const r = { ...(raw as Record<string, unknown>) };
      const fogEnabled = r.fogEnabled === undefined ? true : r.fogEnabled === true;
      // 2026-10-16（P1-2）：旧场景扁平字段（fogColor/fogDensity）迁移到 environment.fog（nested），随后移除旧键
      const legacyColor = typeof r.fogColor === 'string' ? (r.fogColor as string) : undefined;
      const legacyDensity = Number.isFinite(Number(r.fogDensity)) ? Number(r.fogDensity) : undefined;
      delete r.fogColor;
      delete r.fogDensity;
      const rawFog = (r.fog && typeof r.fog === 'object') ? (r.fog as Record<string, unknown>) : {};
      const base = defaultEnv.fog;
      const fog = {
        density: Number.isFinite(Number(rawFog.density)) ? Number(rawFog.density) : (legacyDensity ?? base.density),
        color: typeof rawFog.color === 'string' ? String(rawFog.color) : (legacyColor ?? base.color),
        startDistance: Number.isFinite(Number(rawFog.startDistance)) ? Number(rawFog.startDistance) : base.startDistance,
        endDistance: Number.isFinite(Number(rawFog.endDistance)) ? Number(rawFog.endDistance) : base.endDistance,
        maxOpacity: Number.isFinite(Number(rawFog.maxOpacity)) ? Math.min(1, Math.max(0, Number(rawFog.maxOpacity))) : base.maxOpacity,
        affectedBySky: rawFog.affectedBySky === true,
        heightFalloff: Number.isFinite(Number(rawFog.heightFalloff)) ? Number(rawFog.heightFalloff) : base.heightFalloff,
        heightOffset: Number.isFinite(Number(rawFog.heightOffset)) ? Number(rawFog.heightOffset) : base.heightOffset,
      };
      // 2026-10-16（P2 世界原点）：缺省/非法 → [0,0,0]（避免跨场景残留）
      const wo = (r as Record<string, unknown>).worldOrigin;
      const worldOrigin: [number, number, number] = Array.isArray(wo) && wo.length === 3 && wo.every((n) => Number.isFinite(Number(n)))
        ? [Number(wo[0]), Number(wo[1]), Number(wo[2])]
        : [0, 0, 0];
      return { environment: { ...state.environment, ...r, fog, fogEnabled, worldOrigin } as SceneState['environment'], dirty: false };    }),

  setEffects: (partial, markDirty = true) =>
    set((state) => {
      const prev = state.effects;
      return {
        effects: {
          ...prev,
          ...partial,
          dayNight: partial.dayNight ? { ...prev.dayNight, ...partial.dayNight } : prev.dayNight,
          sky: partial.sky ? { ...prev.sky, ...partial.sky } : prev.sky,
          clouds: partial.clouds ? { ...prev.clouds, ...partial.clouds } : prev.clouds,
          weather: partial.weather ? { ...prev.weather, ...partial.weather } : prev.weather,
          water: partial.water ? { ...prev.water, ...partial.water } : prev.water,
          atmosphere: partial.atmosphere ? { ...prev.atmosphere, ...partial.atmosphere } : prev.atmosphere,
          postprocessing: partial.postprocessing
            ? {
                ...prev.postprocessing,
                // 2026-10-16（P1-1）：标量字段（exposure/resolutionScale）先整体覆盖，再逐子对象合并
                ...partial.postprocessing,
                bloom: partial.postprocessing.bloom ? { ...prev.postprocessing.bloom, ...partial.postprocessing.bloom } : prev.postprocessing.bloom,
                dof: partial.postprocessing.dof ? { ...prev.postprocessing.dof, ...partial.postprocessing.dof } : prev.postprocessing.dof,
                grain: partial.postprocessing.grain ? { ...prev.postprocessing.grain, ...partial.postprocessing.grain } : prev.postprocessing.grain,
                vignette: partial.postprocessing.vignette ? { ...prev.postprocessing.vignette, ...partial.postprocessing.vignette } : prev.postprocessing.vignette,
                ssao: partial.postprocessing.ssao ? { ...prev.postprocessing.ssao, ...partial.postprocessing.ssao } : prev.postprocessing.ssao,
                antialiasing: partial.postprocessing.antialiasing ? { ...prev.postprocessing.antialiasing, ...partial.postprocessing.antialiasing } : prev.postprocessing.antialiasing,
                outline: partial.postprocessing.outline ? { ...prev.postprocessing.outline, ...partial.postprocessing.outline } : prev.postprocessing.outline,
              }
            : prev.postprocessing,
        },
        ...(markDirty ? { dirty: true } : {}),
      };
    }),

  addLight: (light) => {
    const id = generateId();
    set((state) => ({ lights: [...state.lights, { ...light, id }], dirty: true }));
  },

  // 2026-10-17（标绘层 S2）
  addAnnotation: (a) => {
    const id = generateId();
    set((state) => ({ annotations: [...state.annotations, { ...a, id }], dirty: true }));
    return id;
  },

  updateAnnotation: (id, updates) =>
    set((state) => ({ annotations: state.annotations.map((x) => (x.id === id ? { ...x, ...updates } : x)), dirty: true })),

  removeAnnotation: (id) =>
    set((state) => ({ annotations: state.annotations.filter((x) => x.id !== id), dirty: true })),

  setSpikeChart3D: (chart) => set({ spikeChart3D: chart, dirty: true }),

  updateSpikeChart3D: (id, updates) =>
    set((state) => (
      state.spikeChart3D?.id === id
        ? { spikeChart3D: { ...state.spikeChart3D, ...updates }, dirty: true }
        : state
    )),

  setSpikeBoard3D: (board) => set({ spikeBoard3D: board, dirty: true }),

  updateSpikeBoard3D: (id, updates) =>
    set((state) => (
      state.spikeBoard3D?.id === id
        ? { spikeBoard3D: { ...state.spikeBoard3D, ...updates }, dirty: true }
        : state
    )),

  setSpikeHud3D: (hud) => set({ spikeHud3D: hud, dirty: true }),

  updateSpikeHud3D: (id, updates) =>
    set((state) => (
      state.spikeHud3D?.id === id
        ? { spikeHud3D: { ...state.spikeHud3D, ...updates }, dirty: true }
        : state
    )),

  setChart3DObjects: (charts) => set({
    chart3dObjects: charts.map((chart) => createChart3DObject(chart)),
    dirty: false,
  }),

  addChart3D: (chart) => {
    if (shouldRecordHistory()) {
      useHistoryStore.getState().pushSnapshot(
        takeSnapshot(get().models, get().markers, get().subObjectConfigs, { chart3dObjects: get().chart3dObjects }),
      );
    }
    const id = chart.id || generateId();
    const next = createChart3DObject({ ...chart, id });
    set((state) => ({ chart3dObjects: [...state.chart3dObjects, next], dirty: true }));
    return id;
  },

  updateChart3D: (id, updates, recordHistory = true) => {
    if (recordHistory && shouldRecordHistory()) {
      useHistoryStore.getState().pushSnapshot(
        takeSnapshot(get().models, get().markers, get().subObjectConfigs, { chart3dObjects: get().chart3dObjects }),
      );
    }
    set((state) => ({
      chart3dObjects: state.chart3dObjects.map((item) => (item.id === id ? { ...item, ...updates } : item)),
      dirty: true,
    }));
  },

  removeChart3D: (id) => {
    if (shouldRecordHistory()) {
      useHistoryStore.getState().pushSnapshot(
        takeSnapshot(get().models, get().markers, get().subObjectConfigs, { chart3dObjects: get().chart3dObjects }),
      );
    }
    set((state) => ({ chart3dObjects: state.chart3dObjects.filter((item) => item.id !== id), dirty: true }));
  },

  // 2026-10-17（体积元素 S1）
  addVolume: (v) => {
    const id = generateId();
    set((state) => ({ volumes: [...state.volumes, { ...v, id }], dirty: true }));
  },

  updateVolume: (id, updates) =>
    set((state) => ({ volumes: state.volumes.map((x) => (x.id === id ? { ...x, ...updates } : x)), dirty: true })),

  removeVolume: (id) =>
    set((state) => ({ volumes: state.volumes.filter((x) => x.id !== id), dirty: true })),

  updateLight: (id, updates) =>
    set((state) => ({
      lights: state.lights.map((l) => (l.id === id ? { ...l, ...updates } : l)),
      dirty: true,
    })),

  removeLight: (id) =>
    set((state) => ({ lights: state.lights.filter((l) => l.id !== id), dirty: true })),

  toggleLight: (id) =>
    set((state) => ({
      lights: state.lights.map((l) => (l.id === id ? { ...l, enabled: !l.enabled } : l)),
      dirty: true,
    })),

  setAnimation: (anim) =>
    set((state) => ({ animation: { ...state.animation, ...anim }, dirty: true })),

  setClipping: (clip) =>
    set((state) => ({ clipping: { ...state.clipping, ...clip }, dirty: true })),

  setCarousel: (carousel) =>
    set((state) => ({ carousel: { ...state.carousel, ...carousel }, dirty: true })),

  undo: () => {
    // 2026-09-27（审计 FE-C03 修复）：传入「当前状态」——redo 需要它才能还原（原实现重做无效）
    // E0：当前状态含扩展文档态，保证 redo 能恢复环境/特效等
    const current = captureSceneSnapshot();
    const snapshot = useHistoryStore.getState().undo(current);
    if (snapshot) {
      // 2026-09-14：部件配置回滚（ModelViewer 订阅变化后重放 visible/name → 删除的部件恢复显示）
      // E0：扩展字段（环境/特效/灯光等）存在时一并恢复
      set(snapshotRestorePatch(snapshot as ReturnType<typeof takeSnapshot>) as never);
    }
  },

  redo: () => {
    const current = captureSceneSnapshot();
    const snapshot = useHistoryStore.getState().redo(current);
    if (snapshot) {
      set(snapshotRestorePatch(snapshot as ReturnType<typeof takeSnapshot>) as never);
    }
  },

  addMarker: (marker) => {
    const newMarker: Marker = { ...marker, id: generateId() };
    set((state) => ({ markers: [...state.markers, newMarker], dirty: true }));
  },

  setMarkers: (markers) => set({ markers }),

  setFlyLines: (lines) => set({ flyLines: lines }),

  setWaypoints: (waypoints) => set({ waypoints }),

  setModelSubObjects: (modelId, tree) =>
    set((state) => ({ subObjectsMap: { ...state.subObjectsMap, [modelId]: tree } })),
  clearModelSubObjects: (modelId) =>
    set((state) => {
      const next = { ...state.subObjectsMap };
      delete next[modelId];
      return { subObjectsMap: next };
    }),
  /** 2026-09-08｜阶段1：查询构件 uuid 的整条父链（含自身），用于树面板自动展开定位；找不到返回空数组 */
  getAncestorIds: (modelId, uuid) => {
    const tree = get().subObjectsMap[modelId];
    if (!tree) return [];
    const chain: string[] = [];
    const walk = (nodes: SubObjectNode[]): boolean => {
      for (const n of nodes) {
        if (n.uuid === uuid) {
          chain.unshift(n.uuid);
          return true;
        }
        if (walk(n.children)) {
          chain.unshift(n.uuid);
          return true;
        }
      }
      return false;
    };
    walk(tree);
    return chain;
  },

  setBrowseRoute: (route) => set({ browseRoute: route }),
  addBrowsePoint: (p) => set((state) => ({ browseRoute: [...state.browseRoute, p], dirty: true })),
  removeBrowsePoint: (index) =>
    set((state) => ({ browseRoute: state.browseRoute.filter((_, i) => i !== index), dirty: true })),

  addWaypoint: (waypoint) => {
    const wp: Waypoint = { ...waypoint, id: generateId() };
    set((state) => ({ waypoints: [...state.waypoints, wp], dirty: true }));
  },

  removeWaypoint: (id) =>
    set((state) => ({ waypoints: state.waypoints.filter((w) => w.id !== id), dirty: true })),

  setCameraPathPlaying: (playing) => set({ cameraPathPlaying: playing }),

  setCameraPathSpeed: (speed) => set({ cameraPathSpeed: speed }),

  addFlyLine: (line) => {
    const newLine: FlyLine = { ...line, id: generateId(), sceneId: get().currentScene?.id ?? '' };
    set((state) => ({ flyLines: [...state.flyLines, newLine], dirty: true }));
  },

  updateFlyLine: (id, updates) =>
    set((state) => ({
      flyLines: state.flyLines.map((l) => (l.id === id ? { ...l, ...updates } : l)),
      dirty: true,
    })),

  removeFlyLine: (id) =>
    set((state) => ({ flyLines: state.flyLines.filter((l) => l.id !== id), dirty: true })),

  updateCamera: (camera) =>
    set((state) => {
      if (!state.currentScene) return state;
      return {
        currentScene: {
          ...state.currentScene,
          config: {
            ...state.currentScene.config,
            camera: { ...DEFAULT_CAMERA, ...state.currentScene.config.camera, ...camera },
          },
        },
        dirty: true,
      };
    }),

  setMeasurementMode: (mode) => set({ measurementMode: mode, measurementDraftPoints: [] }),

  // 测量点拾取：点击场景时逐点累积，攒够点数后自动生成测量结果
  addMeasurementPoint: (point) =>
    set((state) => {
      const mode = state.measurementMode;
      if (!mode) return state;
      const pts: MeasurementPoint[] = [...state.measurementDraftPoints, { position: point, id: generateId() }];
      const need = mode === 'distance' ? 2 : mode === 'angle' ? 3 : 3;
      if (pts.length < need) return { measurementDraftPoints: pts };
      if (mode === 'distance') {
        const p1 = pts[0].position, p2 = pts[1].position;
        const value = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2 + (p2.z - p1.z) ** 2);
        const measurement: Measurement = { id: `measurement-${Date.now()}`, type: 'distance', points: pts, value, unit: 'm' };
        return { measurementDraftPoints: [], measurementMode: null, measurements: [...state.measurements, measurement], dirty: true };
      }
      if (mode === 'angle') {
        const a = pts[0].position, b = pts[1].position, c = pts[2].position;
        const v1 = { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
        const v2 = { x: c.x - b.x, y: c.y - b.y, z: c.z - b.z };
        const dot = v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
        const m1 = Math.sqrt(v1.x ** 2 + v1.y ** 2 + v1.z ** 2);
        const m2 = Math.sqrt(v2.x ** 2 + v2.y ** 2 + v2.z ** 2);
        const value = m1 * m2 === 0 ? 0 : (Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2)))) * 180) / Math.PI;
        const measurement: Measurement = { id: `measurement-${Date.now()}`, type: 'angle', points: pts, value, unit: '°' };
        return { measurementDraftPoints: [], measurementMode: null, measurements: [...state.measurements, measurement], dirty: true };
      }
      // area：3 点三角形面积
      const [p0, p1, p2] = pts.map((p) => p.position);
      const cross = {
        x: (p1.x - p0.x) * (p2.z - p0.z) - (p1.z - p0.z) * (p2.x - p0.x),
        y: (p1.y - p0.y) * (p2.x - p0.x) - (p1.x - p0.x) * (p2.y - p0.y),
        z: (p1.z - p0.z) * (p2.y - p0.y) - (p1.y - p0.y) * (p2.z - p0.z),
      };
      const value = Math.sqrt(cross.x ** 2 + cross.y ** 2 + cross.z ** 2) / 2;
      const measurement: Measurement = { id: `measurement-${Date.now()}`, type: 'area', points: pts, value, unit: 'm²' };
      return { measurementDraftPoints: [], measurementMode: null, measurements: [...state.measurements, measurement], dirty: true };
    }),

  clearMeasurementDraft: () => set({ measurementDraftPoints: [] }),

  addMeasurement: (measurement) =>
    set((state) => ({ measurements: [...state.measurements, measurement], dirty: true })),

  removeMeasurement: (id) =>
    set((state) => ({
      measurements: state.measurements.filter((m) => m.id !== id),
      dirty: true,
    })),

  setMeasurements: (measurements) => set({ measurements }),

  clearMeasurements: () => set({ measurements: [], dirty: true }),

  toggleVisibility: (id) =>
    set((state) => ({
      visibility: { ...state.visibility, [id]: state.visibility[id] === false ? true : false },
    })),

  toggleLocked: (id) =>
    set((state) => {
      const next = state.locked[id] === true ? false : true;
      return {
        locked: { ...state.locked, [id]: next },
        // 同步到模型数据，保证 SceneCanvas / ModelLibrary / 持久化读取一致
        models: state.models.map((m) => (m.id === id ? { ...m, locked: next } : m)),
        dirty: true,
      };
    }),

  setLocked: (id, locked) =>
    set((state) => ({
      locked: { ...state.locked, [id]: locked },
      models: state.models.map((m) => (m.id === id ? { ...m, locked } : m)),
      dirty: true,
    })),

  updateWaypoint: (id, updates) =>
    set((state) => ({
      waypoints: state.waypoints.map((w) => (w.id === id ? { ...w, ...updates } : w)),
      dirty: true,
    })),

  setBoundedMode: (bounded) => set({ boundedMode: bounded, dirty: true }),
  setDisplayRegion: (r) => set({ displayRegion: r, dirty: true }),
  setRegionSelecting: (v) => set({ regionSelecting: v }),
  setRegionGenSelecting: (v) => set({ regionGenSelecting: v }),
  setPivotOffset: (modelId, offset) =>
    set((s) => {
      const next = { ...s.pivotOffsets };
      if (!offset) delete next[modelId];
      else next[modelId] = offset;
      return { pivotOffsets: next, dirty: true };
    }),
  setPivotOffsets: (map) => set({ pivotOffsets: { ...(map || {}) } }),
  setShowGrid: (show) => set({ showGrid: show, dirty: true }),
  setShowGround: (show) => set({ showGround: show, dirty: true }),

  // ============ 路径相机（轨道相机） ============
  setPathCameras: (cameras) => set({ pathCameras: cameras, dirty: true }),

  addPathCamera: (camera) => {
    const id = generateId();
    set((state) => ({
      pathCameras: [...state.pathCameras, { ...camera, id }],
      dirty: true,
    }));
    return id;
  },

  updatePathCamera: (id, updates) =>
    set((state) => ({
      pathCameras: state.pathCameras.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      dirty: true,
    })),

  removePathCamera: (id) =>
    set((state) => ({
      pathCameras: state.pathCameras.filter((c) => c.id !== id),
      dirty: true,
    })),

  addKeyFrame: (cameraId, frame) =>
    set((state) => ({
      pathCameras: state.pathCameras.map((c) =>
        c.id === cameraId
          ? { ...c, keyFrames: [...c.keyFrames, frame].sort((a, b) => a.time - b.time) }
          : c,
      ),
      dirty: true,
    })),

  updateKeyFrame: (cameraId, index, updates) =>
    set((state) => ({
      pathCameras: state.pathCameras.map((c) =>
        c.id === cameraId
          ? { ...c, keyFrames: c.keyFrames.map((f, i) => (i === index ? { ...f, ...updates } : f)) }
          : c,
      ),
      dirty: true,
    })),

  removeKeyFrame: (cameraId, index) =>
    set((state) => ({
      pathCameras: state.pathCameras.map((c) =>
        c.id === cameraId
          ? { ...c, keyFrames: c.keyFrames.filter((_, i) => i !== index) }
          : c,
      ),
      dirty: true,
    })),
}));

export default useSceneStore;

// ============================================================
// E0：命令总线支持（挂起快照 / 捕获与恢复场景快照）
// ============================================================

/** 事务期间挂起自动快照（避免中间态污染历史栈） */
export function setSceneHistorySuspended(v: boolean) {
  historySuspended = v;
}

export function isSceneHistorySuspended(): boolean {
  return historySuspended;
}

export type SceneSnapshot = ReturnType<typeof takeSnapshot>;

/** 捕获场景文档快照（models/markers/subObjectConfigs + E0 扩展文档态） */
export function captureSceneSnapshot(): SceneSnapshot {
  const s = useSceneStore.getState();
  return takeSnapshot(
    s.models,
    s.markers,
    s.subObjectConfigs,
    snapshotExtra({
      environment: s.environment,
      effects: s.effects,
      lights: s.lights,
      displayRegion: s.displayRegion,
      showGrid: s.showGrid,
      showGround: s.showGround,
      cameraStrategy: s.cameraStrategy,
      savedCamera: s.savedCamera,
      birdsEyeCamera: s.birdsEyeCamera,
      mainCamera: s.mainCamera,
      volumes: s.volumes,
      annotations: s.annotations,
    }),
  );
}

/** 恢复场景快照（事务回滚用；不写历史） */
export function applySceneSnapshot(snapshot: SceneSnapshot) {
  useSceneStore.setState(snapshotRestorePatch(snapshot) as never);
}
